import { sc } from "./reportUtils";

/* ── Data Source Badge ── */
export function DataBadge({ source }) {
  const isLive = source === "live";
  return (
    <span data-testid="data-source-badge" style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", padding: "3px 8px", background: isLive ? "#ECFDF5" : "#FFFBEB", color: isLive ? "#059669" : "#D97706", border: `1px solid ${isLive ? "#A7F3D0" : "#FDE68A"}`, whiteSpace: "nowrap", textTransform: "uppercase", fontFamily: "'JetBrains Mono',monospace" }}>
      {isLive ? "LIVE" : "EST"}
    </span>
  );
}

/* ── Section Header ── */
export function SectionHead({ num, title, subtitle, dataSource }) {
  return (
    <div className="report-section-head" style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        {num && <span style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", fontFamily: "'JetBrains Mono',monospace", letterSpacing: ".08em" }}>{num}</span>}
        <div style={{ height: 1, flex: 1, background: "#E2E8F0" }} />
        {dataSource && <DataBadge source={dataSource} />}
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: "0 0 6px", lineHeight: 1.3, fontFamily: "'Outfit',sans-serif", letterSpacing: "-0.01em" }}>{title}</h2>
      {subtitle && <p style={{ fontSize: 14, color: "#64748B", margin: 0, lineHeight: 1.7, fontFamily: "'IBM Plex Sans',sans-serif" }}>{subtitle}</p>}
    </div>
  );
}

/* ── Signal Progress Bar ── */
export function SigBar({ label, score, detail, critical }) {
  const c = sc(score);
  return (
    <div style={{ padding: "12px 0", borderBottom: "1px solid #F1F5F9" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: "#1E293B", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "'IBM Plex Sans',sans-serif" }}>{label}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: c, fontFamily: "'JetBrains Mono',monospace" }}>{score}</span>
          {critical && <span style={{ fontSize: 9, fontWeight: 700, background: "#FEF2F2", color: "#E11D48", padding: "2px 6px", letterSpacing: ".04em", border: "1px solid #FECACA" }}>CRITICAL</span>}
        </div>
      </div>
      <div style={{ height: 6, background: "#F1F5F9", overflow: "hidden" }} role="progressbar" aria-valuenow={score} aria-valuemin={0} aria-valuemax={100} aria-label={`${label} score: ${score} out of 100`}>
        <div style={{ height: "100%", width: `${score}%`, background: c, transition: "width 1.2s ease" }} />
      </div>
      {detail && <p style={{ fontSize: 12, color: "#64748B", marginTop: 6, lineHeight: 1.6, fontFamily: "'IBM Plex Sans',sans-serif" }}>{detail}</p>}
    </div>
  );
}

/* ── Section Card ── */
export function Section({ id, children, dark = false, style = {} }) {
  return (
    <div id={id} className="report-section" style={{ background: dark ? "#0F172A" : "#fff", border: dark ? "1px solid #1E293B" : "1px solid #E2E8F0", marginBottom: 20, ...style }}>
      <div className="report-section-pad" style={{ padding: "28px 24px" }}>{children}</div>
    </div>
  );
}

/* ── Lock Overlay ── */
export function LockOverlay({ onUnlock, loading, loss }) {
  const fmt$ = n => "$" + Math.floor(n).toLocaleString("en-US");
  return (
    <div style={{ position: "relative", minHeight: 260 }}>
      <div style={{ filter: "blur(8px)", pointerEvents: "none", userSelect: "none", opacity: .4, padding: "20px 0" }}>
        {[85, 100, 90, 70, 95, 60].map((w, i) => (
          <div key={i} style={{ height: i % 2 === 0 ? 18 : 12, background: "#E2E8F0", marginBottom: 10, width: `${w}%` }} />
        ))}
        <div style={{ height: 80, background: "#F1F5F9", marginTop: 8, border: "1px solid #E2E8F0" }} />
      </div>
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 12px" }}>
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", padding: "32px 28px", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,.06)", maxWidth: 380, width: "100%" }}>
          <div style={{ width: 48, height: 48, background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <svg width="22" height="22" fill="none" stroke="#E11D48" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: "#0F172A", margin: "0 0 8px", fontFamily: "'Outfit',sans-serif" }}>This Section is Locked</h3>
          <p style={{ fontSize: 14, color: "#64748B", margin: "0 0 6px", lineHeight: 1.6, fontFamily: "'IBM Plex Sans',sans-serif" }}>Your personalized fix plan is ready.</p>
          {loss > 0 && <p style={{ fontSize: 13, color: "#E11D48", fontWeight: 600, margin: "0 0 16px" }}>Every day you wait costs ~{fmt$(loss / 30)}</p>}
          <button onClick={onUnlock} disabled={loading} data-testid="lock-overlay-btn"
            style={{ background: "#002FA7", color: "#fff", fontWeight: 600, fontSize: 15, padding: "14px 24px", border: "none", cursor: loading ? "wait" : "pointer", width: "100%", fontFamily: "'IBM Plex Sans',sans-serif" }}>
            {loading ? "Processing..." : "Unlock Full Report — $27"}
          </button>
          <p style={{ fontSize: 12, color: "#94A3B8", marginTop: 10, fontFamily: "'IBM Plex Sans',sans-serif" }}>One-time payment &middot; Instant access</p>
        </div>
      </div>
    </div>
  );
}
